import { formatUnits } from 'viem';

import { tokenFormatter, usdFormatter } from '@/lib/formatting';

import { fontStyles } from '@/styles/typography';

import { ProtocolVersion } from '@/utils/0x/history/types';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { MATCHA_LIMIT_ORDER } from '@/utils/liquidity-sources';
import { isUsdStableAddress } from '@/utils/multichain';

import { LimitOrderItem } from '../../utils/0x.types';
import { TRADE_STATUS, TradeDetails } from '../Modal/TradeDetailsModal';
import type { TradeDetailsSelection } from './types';

export const getTradeDetailsRouteIdentity = ({
  orderId,
  transactionHash,
  funderAddress,
}: TradeDetailsSelection): { txHash: string; funderAddress?: string } | undefined => {
  if (orderId && !funderAddress) {
    throw new Error(`Definitive order ${orderId} is missing funderAddress`);
  }
  const txHash = orderId ?? transactionHash;
  return txHash ? { txHash, ...(funderAddress ? { funderAddress } : {}) } : undefined;
};

export const getTradeDetails = (limitOrderItem: LimitOrderItem): TradeDetails => {
  const { makerTokenResult, takerTokenResult } = limitOrderItem;
  const tradeDetails: TradeDetails = {
    ...limitOrderItem,
    makerAmount: makerTokenResult
      ? Number(formatUnits(BigInt(limitOrderItem.makerAmount), makerTokenResult.decimals))
      : 0,
    takerAmount: takerTokenResult
      ? Number(formatUnits(BigInt(limitOrderItem.takerAmount), takerTokenResult.decimals))
      : 0,
    status: TRADE_STATUS.PENDING,
    liquiditySource: MATCHA_LIMIT_ORDER,
    advancedOrderType: limitOrderItem.advancedOrderType ?? 'limit',
    timestamp: limitOrderItem.createdAt,
    expiry: Number(limitOrderItem.expiry) * 1000,
    tradeId: -1,
    protocolVersion: 'Settler' as ProtocolVersion,
  };

  return tradeDetails;
};

/**
 * Formats a limit order/trade price from already human-readable maker/taker
 * amounts. When one side is a USD stablecoin the non-stable token is priced in
 * USD; otherwise the price is shown as a taker/maker token ratio.
 */
export const getLimitDisplayPrice = ({
  makerAmount,
  takerAmount,
  makerToken,
  takerToken,
  chainId,
  makerTokenResult,
  takerTokenResult,
}: {
  makerAmount: number;
  takerAmount: number;
  makerToken: string;
  takerToken: string;
  chainId: number | undefined;
  makerTokenResult: TokenResult | null | undefined;
  takerTokenResult: TokenResult | null | undefined;
}): string => {
  const makerIsStable = isUsdStableAddress(makerToken, chainId);
  const takerIsStable = isUsdStableAddress(takerToken, chainId);

  // When one side is a USD stablecoin, price the non-stable token in USD:
  // (stablecoin amount) / (non-stable amount).
  if (makerIsStable || takerIsStable) {
    const usdPrice = makerIsStable ? makerAmount / takerAmount : takerAmount / makerAmount;
    if (!Number.isFinite(usdPrice)) return '-';
    return usdFormatter.format(usdPrice, true);
  }

  if (!makerAmount) return '-';
  return `${tokenFormatter.format(takerAmount / makerAmount)} ${takerTokenResult?.symbol ?? ''} / ${makerTokenResult?.symbol ?? ''}`;
};

export const getLimitOrderDisplayPrice = (limitOrderItem: LimitOrderItem) => {
  const trade = getTradeDetails(limitOrderItem);
  const { makerToken, takerToken, chainId, takerTokenResult, makerTokenResult } = limitOrderItem;
  return getLimitDisplayPrice({
    makerAmount: trade.makerAmount,
    takerAmount: trade.takerAmount,
    makerToken,
    takerToken,
    chainId,
    makerTokenResult,
    takerTokenResult,
  });
};

type CellKey = string | number | symbol | undefined;

type CellOptions = {
  isSkeleton?: boolean;
  className?: string;
};

const headerPadding: Record<string, string> = {
  default: 'px-2 py-3.5 ',
  sellAmount: 'py-3.5 pl-0 tablet:pr-2',
  buyAmount: 'py-3.5 pr-8 tablet:pr-2',
  selection: 'px-2',
  action: 'pr-4',
};

const headerVisibility: Record<string, string> = {
  default: 'table-cell',
  expiry: 'hidden desktop:table-cell',
  date: 'hidden desktop:table-cell',
  filled: 'hidden tablet:table-cell',
  action: 'hidden tablet:table-cell tablet:w-16',
  network: 'hidden tablet:table-cell',
  status: 'hidden tablet:table-cell',
  type: 'hidden tablet:table-cell',
};

const dataPadding: Record<string, string> = {
  default: 'px-2 py-3 tablet:px-2 desktop:px-2',
  sellAmount: 'py-3 pl-0 tablet:pl-0.5 tablet:pr-1',
  buyAmount: 'py-3 pr-8 tablet:pl-0 tablet:pr-2',
  selection: 'pl-8 tablet:pl-8 pr-2',
  action: '',
};

const dataVisibility: Record<string, string> = {
  default: 'table-cell',
  expiry: 'hidden desktop:table-cell',
  date: 'hidden desktop:table-cell',
  filled: 'hidden tablet:table-cell',
  action: 'hidden tablet:table-cell tablet:w-16 desktop:w-16',
  network: 'hidden tablet:table-cell',
  status: 'hidden tablet:table-cell',
  type: 'hidden tablet:table-cell',
};

const knownCells = new Set([
  'sellAmount',
  'buyAmount',
  'expiry',
  'date',
  'filled',
  'action',
  'network',
  'status',
  'type',
  'selection',
]);

const normalizeCellKey = (cell: CellKey) => {
  if (typeof cell === 'string' && knownCells.has(cell)) {
    return cell;
  }
  return 'default';
};

export const getHeaderCellClass = (cell: CellKey, options: CellOptions = {}) => {
  const key = normalizeCellKey(cell);
  return classNames(
    'align-middle text-left first:pl-8 tablet:first:pl-8',
    fontStyles({ variant: 'LABEL_SM', class: 'text-token-gray-800' }),
    headerPadding[key] ?? headerPadding.default,
    headerVisibility[key] ?? headerVisibility.default,
    options.isSkeleton && key === 'sellAmount' ? 'pl-8 tablet:pl-8' : undefined,
    options.className,
  );
};

export const getDataCellClass = (cell: CellKey, options: CellOptions = {}) => {
  const key = normalizeCellKey(cell);
  return classNames(
    'align-middle text-left',
    dataPadding[key] ?? dataPadding.default,
    dataVisibility[key] ?? dataVisibility.default,
    options.isSkeleton && key === 'sellAmount' ? 'pl-8 tablet:pl-8' : undefined,
    options.className,
  );
};

export type { CellOptions };
